import { useState, useCallback } from 'react'
import Link from 'next/link'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import styles from '../ktechpioneers.module.scss'

interface IFaqItem {
  question: string
  answer?: string
  externalUrl1?: string
  urlName1?: string
  externalUrl2?: string
  urlName2?: string
  answerList?: string[]
  category: string
}

interface PioneersFAQProps {
  contents: Record<string, Omit<IFaqItem, 'category'>[]>
  contentsTabList: { label: string; value: string }[]
}

const PioneersFAQ = ({
  contents,
  contentsTabList,
}: PioneersFAQProps) => {
  const [faqTab, setFaqTab] = useState<string>('default')

  const flattenedFaqList: IFaqItem[] = Object.entries(contents).flatMap(([category, items]) =>
    items.map(item => ({
      ...item,
      category,
    })),
  )

  const filteredFaqs =
    faqTab === 'all' ? flattenedFaqList : flattenedFaqList.filter(faq => faq.category === faqTab)

  const [openQuestions, setOpenQuestions] = useState<Set<string>>(() => {
    const first = filteredFaqs[0]?.question
    return first ? new Set([first]) : new Set()
  })

  const toggleOpen = useCallback((question: string) => {
    setOpenQuestions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(question)) {
        newSet.delete(question)
      } else {
        newSet.add(question)
      }
      return newSet
    })
  }, [])

  const onTabChange = (nextTab: string) => {
    setFaqTab(nextTab)
    const nextList =
      nextTab === 'all' ? flattenedFaqList : flattenedFaqList.filter(f => f.category === nextTab)
    const first = nextList[0]?.question
    setOpenQuestions(first ? new Set([first]) : new Set())
  }

  return (
    <div>
      {/* FAQ 탭 목록 */}
      <ul
        className="mx-auto mb-10 flex max-w-[300px] flex-wrap items-center justify-center gap-1.5 pc:mb-16 pc:max-w-none pc:flex-nowrap pc:gap-2">
        {contentsTabList.map(tabItem => {
          const isActive = tabItem.value === faqTab
          return (
            <li
              key={tabItem.value}
              onClick={() => onTabChange(tabItem.value)}
              className={`${isActive
                ? 'border-[#0080FF] bg-[#0080FF] font-bold text-white'
                : 'border-white/30 bg-transparent text-white/70'
                } cursor-pointer rounded-full border px-4 py-1.5 text-xs pc:px-6 pc:py-2 pc:text-sm`}
            >
              {tabItem.label}
            </li>
          )
        })}
        <li className="bg-[#00388F] rounded-lg cursor-pointer border border-[#00388F] text-white px-4 py-1.5 text-xs pc:px-6 pc:py-2 pc:text-sm">
          <a
            href="https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/KTP_FAQ.docx"
            download
            className="w-full h-full flex items-center justify-center gap-1">
            FAQ 다운로드
            <img src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/icon_download.png" className="w-5 h-auto object-contain" alt="download" />
          </a>
        </li>
      </ul>

      {/* FAQ 리스트 */}
      <ul className="mx-auto flex flex-col">
        {filteredFaqs.map(faq => {
          const isOpen = openQuestions.has(faq.question)
          return (
            <li
              key={faq.question}
              className={`${isOpen ? 'bg-white/10' : 'bg-transparent'
                } w-full overflow-hidden border-t border-white/20 backdrop-blur-sm transition-all duration-300 hover:bg-white/10`}
            >
              <div
                onClick={() => toggleOpen(faq.question)}
                className={`${styles.faq_item_title_wrap} ${isOpen ? 'pc:pb-3.5' : 'pc:pb-5'
                  } flex w-full cursor-pointer flex-row gap-x-3 px-4 pb-3 pt-3 pc:px-6 pc:pt-5`}
              >
                <div className="flex-1">
                  <h6
                    className={`${styles.faq_item_title} inline-block text-sm font-bold leading-normal text-white pc:text-lg`}
                  >
                    {faq.question}
                  </h6>
                </div>
                <ChevronDownIcon
                  className={`${isOpen ? 'rotate-180 transform' : ''
                    } h-auto w-4 shrink-0 text-white/70 pc:w-6`}
                />
              </div>

              {isOpen && (
                <div className="flex flex-col gap-1 pb-3 pl-[42px] pr-8 pc:gap-2 pc:pb-5 pc:pl-[56px] pc:pr-[60px]">
                  {faq.answer && (
                    <span className="block whitespace-pre-line text-xs leading-normal text-white/70 pc:text-base">
                      {faq.answer}
                      {faq.externalUrl1 && (
                        <>
                          <br />-{' '}
                          <Link href={faq.externalUrl1} target="_blank" className="text-[#3385FF] underline">
                            {faq.urlName1}
                          </Link>
                        </>
                      )}
                      {faq.externalUrl2 && (
                        <>
                          <br />-{' '}
                          <Link href={faq.externalUrl2} target="_blank" className="text-[#3385FF] underline">
                            {faq.urlName2}
                          </Link>
                        </>
                      )}
                    </span>
                  )}
                  {!!faq.answerList?.length && (
                    <div className="flex flex-col gap-0.5 pc:gap-1">
                      {faq.answerList!.map(el => (
                        <p
                          key={el}
                          className={`${styles.faq_item_answer_list_text} block whitespace-pre-line text-xs leading-normal text-white/70 pc:text-base`}
                        >
                          {el}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default PioneersFAQ
